import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { WebsocketService } from '../../../services/websocket/websocket.service';
import { ConnectionStatusService } from '../../../services/connection-status.service';
import { distinctUntilChanged, filter, map, pluck, switchMap, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router, RouterLink, UrlTree } from '@angular/router';
import { IRestaurantInfo } from '../../../interfaces/restaurant';
import { HeaderMenuItem } from '../../../interfaces/header-links';
import { TableService } from '../../../services/table/table.service';
import { AuthService } from '../../../services/auth/auth.service';
import { UserRoles } from '../../../shared/enums/userRoles';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';


@UntilDestroy()
@Component({
  selector: 'app-restaurant-layout',
  templateUrl: './restaurant-layout.component.html',
  styleUrls: ['./restaurant-layout.component.scss']
})
export class RestaurantLayoutComponent implements OnInit, OnDestroy {

  private readonly allMenuLinks: HeaderMenuItem[] = [
    {
      route: 'tables',
      title: 'navigation.tables',
      access: true
    },
    {
      route: 'menu',
      title: 'navigation.menu',
      access: true
    },
    {
      route: 'restaurant-account',
      title: 'navigation.restaurantAccount',
      access: true
    },
    {
      route: 'business-hours',
      title: 'navigation.businessHours',
      access: true
    },
    {
      route: 'integrations',
      title: 'navigation.integrations',
      access: true
    },
    {
      route: 'waiters',
      title: 'navigation.waiters',
      access: true
    },
    {
      route: 'happy-hours',
      title: 'navigation.happyHours',
      access: true
    },
  ];
  public menuLinks: HeaderMenuItem[] = this.allMenuLinks.filter(link => link.route !== 'integrations');

  private restaurantSubject = new BehaviorSubject<IRestaurantInfo | null>(null);
  private isChainArea$ = this.authService.areas$.pipe(
    map(area => ['chains', 'chain'].includes(area))
  );

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private websocketService: WebsocketService,
    private connectionStatus: ConnectionStatusService,
    private tableService: TableService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.activatedRoute.data.pipe(pluck('restaurant')),
      this.websocketService.authorized$.pipe(filter(status => status)),
    ]).pipe(
      switchMap(([restaurant]: [IRestaurantInfo, boolean]) => {
        const currentRestaurant = this.restaurantSubject.value;

        if (currentRestaurant) {
          this.websocketService.unsubscribeFromRestaurant(currentRestaurant.id);
        }

        this.restaurantSubject.next(restaurant);
        this.websocketService.subscribeToRestaurant(restaurant.id);

        if (restaurant.meta.userRole === UserRoles.waiter) {
          this.menuLinks = [];
        } else {
          const canViewIntegrations = this.authService.isSuperAdmin() || this.authService.isViewer(restaurant);

          this.menuLinks = this.allMenuLinks.filter(link => link.route !== 'integrations' || canViewIntegrations);
        }

        return this.authService.areas$;
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  ngOnDestroy(): void {
    const currentRestaurant = this.restaurantSubject.value;

    if (currentRestaurant) {
      this.websocketService.unsubscribeFromRestaurant(currentRestaurant.id);
    }

    this.tableService.items = [];
  }

  get restaurant(): IRestaurantInfo | null {
    return this.restaurantSubject.value;
  }

  get restaurant$(): Observable<IRestaurantInfo | null> {
    return this.restaurantSubject.asObservable();
  }

  get searchValue$(): Observable<string | undefined> {
    return this.restaurantSubject.pipe(
      map(restaurant => {

        if (!restaurant?.chainId) {
          return undefined;
        }

        return sessionStorage.getItem('chain.' + restaurant.chainId.toString() + '.restaurants') ?? undefined;
      }),
      distinctUntilChanged()
    );
  }

  get backLink$(): Observable<UrlTree | null> {
    return combineLatest([
      this.restaurantSubject,
      this.searchValue$
    ]).pipe(
      map(([restaurant, searchValue]) => {

        if (restaurant?.meta.userRole === UserRoles.waiter || !restaurant?.chainId) {
          return null;
        }

        return this.router.createUrlTree(
          ['/chains', restaurant.chainId.toString(), 'restaurants'],
          {
            queryParams: { s: searchValue || undefined },
            fragment: restaurant.id.toString(),
          }
        );
      }),
      distinctUntilChanged()
    );
  }

  get isOnline(): boolean {
    return this.connectionStatus.online$.getValue();
  }

  get isWSConnected(): boolean {
    return this.websocketService.connected$.getValue();
  }

  get currentLanguage(): string {
    return this.translate.currentLang || this.translate.defaultLang;
  }

  get availableLanguages(): string[] {
    return this.translate.getLangs();
  }

  public handlerChangeLanguage(event: Event): void {
    if (event.target instanceof HTMLSelectElement) {
      this.translate.use(event.target.value);
      localStorage.setItem('lang', event.target.value);
    }
  }
}
